import { HOMEWORK_DEFAULT_TITLE_KEYS } from '@cc/schema';
import { useCustom, useTranslate } from '@refinedev/core';
import { Button, Card, Collapse, Space, Table, Tag, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import {
  EmptyState,
  ToolAssignButton,
  ToolSection,
  fmtDay,
} from '../../components/ToolSection';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { useRefreshClientPage } from '../clients/refreshClientPage';
import type { AssignDefaults, AssignPreset } from '../homework/AssignHomeworkDrawer';
import { MiChangeDrawer } from './MiChangeDrawer';
import {
  ANY_GOAL,
  CHANGE_GOALS_FIELDS,
  MI_ASSIGN_WHATS,
  STATUS_OPEN,
  VALUES_FIELDS,
  balanceBoxes,
  orderedChanges,
  readinessText,
  rulerRows,
  uricaOf,
  valueChoices,
  writtenInSession,
  type Change,
  type ChangeGoals,
  type RulersAt,
  type ValuesRow,
} from './model';

/**
 * E49 · ClientChangeGoals + SetChangeGoal (with the client) + AssignHomework (the rulers before the session) —
 * "Client Hub — Motivation": the client page > Skills & exercises > Motivational interviewing (design PORTAL-SECTIONS-2026-10-22 §2.6).
 * The stage of change (URICA) once, person-wide, in a card of its own; then each change the client is considering — open first, the
 * first open one expanded — with the rulers over time, their own words, the change-talk journal, the latest balance and plan.
 * Set a change with the client = a header button opening a drawer; Assign before the session = the one Assign button.
 *
 * Visible through a relation with VIEW permission, and only for a client who shares their own work (`show.tsx` renders the
 * not-shared line for `ownWork` tools; the server refuses with MI_NOT_YOUR_CLIENT otherwise and the message is shown as the
 * API sent it). DISPLAY ONLY for the read model: the client's own words, no verdict, no advice.
 */
type Props = { clientId: string; clientName: string; locale: 'en' | 'ru' };
type Tx = (key: string, options?: Record<string, unknown>) => string;

const TID = 'cc-client-motivation';

export const ClientMotivationTab = ({ clientId, clientName, locale }: Props) => {
  const t = useTranslate() as unknown as Tx;
  const day = (iso: string) => fmtDay(iso, locale);
  // the pill's count and "new", and the digest, re-read after a write of this section (E57 — same as the header's Assign)
  const refreshClientPage = useRefreshClientPage();

  // ── the read model ────────────────────────────────────────────────────────────────────────
  const goalsQuery = useCustom<ChangeGoals>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientChangeGoals',
      fields: CHANGE_GOALS_FIELDS,
      variables: { subject: { name: 'subject', type: 'ID!', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const goals = goalsQuery.data?.data ?? null;
  const changes = useMemo(() => orderedChanges(goals?.changes ?? []), [goals]);
  const openChanges = changes.filter((c) => c.status === STATUS_OPEN);
  const urica = uricaOf(goals);
  const loadError = goalsQuery.error
    ? graphqlErrorInfo(goalsQuery.error).message ?? t('mi.hub.loadError')
    : null;

  // ── the client's values, for the chips ────────────────────────────────────────────────────
  const valuesQuery = useCustom<ValuesRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'valuesProfile',
      fields: VALUES_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID', value: clientId },
        language: { name: 'language', type: 'String', value: locale },
      },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const choices = valueChoices(
    valuesQuery.data?.data,
    // the value's NAME — a card line is "NAME — meaning" (E36's own label, `valueLabel` on the phone)
    (v) => v.cardText?.split('\n')[0]?.split(' — ')[0]?.trim() || v.value,
  );

  // ── the drawer and the open panels ────────────────────────────────────────────────────────
  const [setOpen, setSetOpen] = useState(false);
  // the first open change is expanded once the changes load; a change set just now is expanded too
  const [active, setActive] = useState<string[] | null>(null);
  useEffect(() => {
    if (active === null && goals) {
      setActive(openChanges[0] ? [openChanges[0].goalId] : []);
    }
  }, [goals, active, openChanges]);

  // ── Assign before the session: the three pre-work kinds, any change or one of the open ones ─
  const assignDefaultsOf = (w: (typeof MI_ASSIGN_WHATS)[number]): AssignDefaults => ({
    title: t(HOMEWORK_DEFAULT_TITLE_KEYS[w]), // Q-ru-3 A: left as it is, no title is stored
    reminderPolicy: 'default',
    deadline: { shape: 'due', defaultHour: 9 },
    ref: {
      label: t('mi.hub.assign.goal'),
      any: { value: ANY_GOAL, label: t('mi.hub.assign.anyGoal') },
      options: openChanges.map((c) => ({ value: c.goalId, label: c.label })),
      value: ANY_GOAL,
    },
  });
  const assignPreset: AssignPreset = {
    kind: 'tool',
    target: MI_ASSIGN_WHATS[0],
    targets: MI_ASSIGN_WHATS,
    defaults: Object.fromEntries(
      MI_ASSIGN_WHATS.map((w) => [w, assignDefaultsOf(w)]),
    ),
  };

  // ── pieces ────────────────────────────────────────────────────────────────────────────────
  // ── pieces ────────────────────────────────────────────────────────────────────────────────
  const lines = (items: string[], testId: string) => (
    <ul style={{ margin: 0, paddingLeft: 20 }} data-testid={testId}>
      {items.map((x, i) => (
        <li key={`${i}-${x}`}>{x}</li>
      ))}
    </ul>
  );

  const rulersTable = (c: Change, i: number) => {
    const rows = rulerRows(c.rulersOverTime);
    if (rows.length === 0) {
      return (
        <Typography.Text
          type="secondary"
          data-testid={`${TID}-change-${i}-rulers-empty`}
        >
          {t('mi.hub.rulersEmpty')}
        </Typography.Text>
      );
    }
    const cell = (
      r: RulersAt,
      ruler: 'importance' | 'confidence' | 'readiness',
    ) => {
      const focused = r.focusedRuler === ruler;
      return (
        <span
          data-focused={focused ? 'true' : 'false'}
          style={focused ? { fontWeight: 700 } : undefined}
        >
          {r[ruler]}
        </span>
      );
    };
    return (
      <div data-testid={`${TID}-change-${i}-rulers`}>
        <Table<RulersAt>
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          rowKey={(r) => r.occurredAt}
          dataSource={rows}
          onRow={(_r, j) =>
            ({
              'data-testid': `${TID}-change-${i}-rulers-row-${j ?? 0}`,
            } as Record<string, string>)
          }
          columns={[
            {
              title: t('mi.hub.date'),
              key: 'date',
              render: (_: unknown, r: RulersAt) => day(r.occurredAt),
            },
            {
              title: t('mi.ruler.importance'),
              key: 'importance',
              render: (_: unknown, r: RulersAt) => cell(r, 'importance'),
            },
            {
              title: t('mi.ruler.confidence'),
              key: 'confidence',
              render: (_: unknown, r: RulersAt) => cell(r, 'confidence'),
            },
            {
              title: t('mi.ruler.readiness'),
              key: 'readiness',
              render: (_: unknown, r: RulersAt) => cell(r, 'readiness'),
            },
            {
              title: t('mi.rulers.focus'),
              key: 'focus',
              render: (_: unknown, r: RulersAt) =>
                r.focusedRuler ? t(`mi.ruler.${r.focusedRuler}`) : '',
            },
          ]}
        />
      </div>
    );
  };

  const planView = (c: Change, i: number) => {
    const p = c.plan;
    if (!p) return null;
    return (
      <div data-testid={`${TID}-change-${i}-plan`}>
        <Typography.Title level={5}>
          {t('mi.hub.plan')} · {day(p.writtenAt)}
        </Typography.Title>
        <Typography.Paragraph
          strong
          data-testid={`${TID}-change-${i}-plan-changes`}
        >
          {p.changes}
        </Typography.Paragraph>
        {p.reasons.length > 0 ? (
          <>
            <Typography.Text type="secondary">
              {t('mi.plan.reasons')}
            </Typography.Text>
            {lines(p.reasons, `${TID}-change-${i}-plan-reasons`)}
          </>
        ) : null}
        {p.planSteps.length > 0 ? (
          <>
            <Typography.Text type="secondary">
              {t('mi.plan.steps')}
            </Typography.Text>
            {lines(
              p.planSteps.map((s) =>
                s.when ? `${s.step} — ${s.when}` : s.step,
              ),
              `${TID}-change-${i}-plan-steps`,
            )}
          </>
        ) : null}
        {p.helpers.length > 0 ? (
          <>
            <Typography.Text type="secondary">
              {t('mi.plan.helpers')}
            </Typography.Text>
            {lines(p.helpers, `${TID}-change-${i}-plan-helpers`)}
          </>
        ) : null}
        {p.obstacles.length > 0 ? (
          <>
            <Typography.Text type="secondary">
              {t('mi.plan.obstacles')}
            </Typography.Text>
            {lines(
              p.obstacles.map((o) =>
                o.response ? `${o.obstacle} — ${o.response}` : o.obstacle,
              ),
              `${TID}-change-${i}-plan-obstacles`,
            )}
          </>
        ) : null}
        {p.signsOfSuccess ? (
          <>
            <br />
            <Typography.Text type="secondary">
              {t('mi.plan.signsOfSuccess')}
            </Typography.Text>
            <Typography.Paragraph data-testid={`${TID}-change-${i}-plan-signs`}>
              {p.signsOfSuccess}
            </Typography.Paragraph>
          </>
        ) : null}
      </div>
    );
  };

  const balanceView = (c: Change, i: number) => {
    if (!c.balance) return null;
    return (
      <div data-testid={`${TID}-change-${i}-balance`}>
        <Typography.Title level={5}>
          {t('mi.hub.balance')} · {day(c.balance.writtenAt)}
        </Typography.Title>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 8,
          }}
        >
          {balanceBoxes(c.balance).map(({ box, lines: boxLines }) => (
            <Card
              key={box}
              size="small"
              // founder 2026-10-20: antd clips a card title to one line with an ellipsis; these titles are sentences — let them wrap
              title={t(`mi.balance.${box}`)}
              styles={{
                title: {
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'clip',
                  overflowWrap: 'anywhere',
                },
              }}
              data-testid={`${TID}-change-${i}-balance-${box}`}
            >
              {boxLines.length > 0
                ? lines(boxLines, `${TID}-change-${i}-balance-${box}-lines`)
                : null}
            </Card>
          ))}
        </div>
      </div>
    );
  };

  const changeItem = (c: Change, i: number) => {
    const closed = c.status !== STATUS_OPEN;
    const rows = rulerRows(c.rulersOverTime);
    const last = rows[rows.length - 1];
    return {
      key: c.goalId,
      forceRender: true,
      'data-testid': `${TID}-change-${i}`,
      'data-status': c.status,
      'data-goal-id': c.goalId,
      label: (
        <Space wrap size={[8, 4]}>
          <strong data-testid={`${TID}-change-${i}-label`}>{c.label}</strong>
          {closed ? (
            <Tag data-testid={`${TID}-change-${i}-closed`}>
              {t('mi.hub.closed', {
                date: c.closedAt ? day(c.closedAt) : '',
                outcome: c.outcome ? t(`mi.outcome.${c.outcome}`) : '',
              })}
            </Tag>
          ) : null}
          {writtenInSession(c) ? (
            <Tag data-testid={`${TID}-change-${i}-set-in-session`}>
              {t('mi.hub.setBy')}
            </Tag>
          ) : null}
          {c.linkedValues.length > 0 ? (
            <Space wrap size={4} data-testid={`${TID}-change-${i}-values`}>
              {c.linkedValues.map((v) => (
                <Tag key={v}>{v}</Tag>
              ))}
            </Space>
          ) : null}
          <Typography.Text type="secondary" data-testid={`${TID}-change-${i}-summary`}>
            {last
              ? t('mi.hub.summary', {
                  importance: last.importance,
                  confidence: last.confidence,
                  readiness: last.readiness,
                  date: day(last.occurredAt),
                })
              : t('mi.hub.rulersEmpty')}
          </Typography.Text>
        </Space>
      ),
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {closed && c.closingNote ? (
        <Typography.Text
          type="secondary"
          data-testid={`${TID}-change-${i}-closing-note`}
        >
          {c.closingNote}
        </Typography.Text>
      ) : null}

      <div>
        <Typography.Title level={5}>{t('mi.hub.rulers')}</Typography.Title>
        {rulersTable(c, i)}
      </div>

      <div>
        <Typography.Title level={5}>
          {t('mi.hub.changeTalk')}
        </Typography.Title>
        {c.changeTalk.length > 0 ? (
          lines(c.changeTalk, `${TID}-change-${i}-talk`)
        ) : (
          <Typography.Text
            type="secondary"
            data-testid={`${TID}-change-${i}-talk-empty`}
          >
            {t('mi.hub.changeTalkEmpty')}
          </Typography.Text>
        )}
      </div>

      {c.journal.length > 0 ? (
        <div data-testid={`${TID}-change-${i}-journal`}>
          <Typography.Title level={5}>
            {t('mi.hub.journal')}
          </Typography.Title>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {c.journal.map((j) => (
              <li
                key={j.noteId}
                data-testid={`${TID}-change-${i}-journal-line`}
              >
                <Typography.Text type="secondary">
                  {day(j.noticedAt)}
                  {j.kind ? ` · ${t(`mi.talk.kinds.${j.kind}`)}` : ''}
                  {' — '}
                </Typography.Text>
                {j.talk}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {balanceView(c, i)}
      {planView(c, i)}
    </Space>
      ),
    };
  };

  const empty = Boolean(goals) && changes.length === 0;

  return (
    <>
      <ToolSection
        testId={`${TID}-tab`}
        title={t('mi.hub.title')}
        purpose={t('mi.hub.intro')}
        actions={
          <>
            {empty ? null : (
              <Button
                data-testid={`${TID}-goal-open`}
                onClick={() => setSetOpen(true)}
              >
                {t('mi.hub.goal.title')}
              </Button>
            )}
            <ToolAssignButton
              clientId={clientId}
              clientName={clientName}
              preset={assignPreset}
              onAssigned={() => goalsQuery.refetch()}
            />
          </>
        }
        loadError={loadError}
        loadErrorTestId={`${TID}-load-error`}
      >
        {/* the stage of change — person-wide, once, a card with no title */}
        {urica ? (
          <Card
            size="small"
            data-testid={`${TID}-urica`}
            data-stage={urica.stage}
            data-role="result"
          >
            <Typography.Text strong data-testid={`${TID}-urica-stage`}>
              {t('mi.hub.urica', {
                stage: t(`mi.stage.${urica.stage}`),
                readiness: readinessText(urica),
              })}
            </Typography.Text>
            <br />
            <Typography.Text type="secondary" data-testid={`${TID}-urica-taken`}>
              {t('mi.hub.uricaTaken', { date: day(urica.takenAt) })}
            </Typography.Text>
          </Card>
        ) : null}

        {empty ? (
          <EmptyState
            text={t('mi.hub.empty')}
            primary={
              <Button
                type="primary"
                data-testid={`${TID}-goal-open`}
                onClick={() => setSetOpen(true)}
              >
                {t('mi.hub.goal.title')}
              </Button>
            }
          />
        ) : null}

        {changes.length > 0 ? (
          <Collapse
            activeKey={active ?? []}
            onChange={(k) => setActive(Array.isArray(k) ? k : [k])}
            items={changes.map(changeItem) as never}
          />
        ) : null}
      </ToolSection>

      <MiChangeDrawer
        open={setOpen}
        clientId={clientId}
        choices={choices}
        onClose={() => setSetOpen(false)}
        onSaved={(id) => {
          setSetOpen(false);
          if (id) setActive([id]);
          goalsQuery.refetch();
          refreshClientPage('assign');
        }}
      />
    </>
  );
};
